import React, { useState, useEffect, useMemo } from 'react';
import { Provider, AuditLog, UserRole } from '../types';
import {
  ShieldCheck,
  UserCheck,
  Database,
  Lock,
  Search,
  Activity,
  AlertOctagon,
  RefreshCw,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Radio,
  FileCheck2,
  Filter,
  User,
  FileText,
  Clock,
  Eye,
  CheckCircle2,
  X
} from 'lucide-react';
import { PulseIntelligenceMark } from './PulseIntelligenceMark';
import { ProviderDetailModal } from './ProviderDetailModal';
import { AdminConnectionsSetupView } from './AdminConnectionsSetupView';
import { ProviderManagementView } from './ProviderManagementView';
import { OwnerAiDataUsageView } from './OwnerAiDataUsageView';
import { getCanonicalAuditCategory } from '../utils/auditCategories';

interface AdminSystemViewProps {
  userRole: UserRole;
  initialTab?: 'supervision' | 'admin' | 'connections' | 'ai-usage';
  subTab?: 'supervision' | 'admin' | 'connections' | 'ai-usage';
  onNavigateToTab?: (tab: string) => void;
}

export const AdminSystemView: React.FC<AdminSystemViewProps> = ({
  userRole,
  initialTab,
  subTab = 'admin',
  onNavigateToTab
}) => {
  const resolveTab = () => {
    if (userRole === 'SUPERVISOR') return 'supervision';
    if (initialTab === 'connections' || subTab === 'connections') return 'connections';
    if (initialTab === 'supervision' || subTab === 'supervision') return 'supervision';
    if ((initialTab === 'ai-usage' || subTab === 'ai-usage') && userRole === 'OWNER') return 'ai-usage';
    if (initialTab === 'admin' || subTab === 'admin') return 'audit';
    return 'audit';
  };

  const [activeTab, setActiveTab] = useState<'connections' | 'supervision' | 'audit' | 'ai-usage'>(resolveTab());
  const [providers, setProviders] = useState<Provider[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [systemHealth, setSystemHealth] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);

  // Audit filter and expansion state
  const [auditSearch, setAuditSearch] = useState('');
  const [auditCategory, setAuditCategory] = useState<string>('all');
  const [expandedLogIds, setExpandedLogIds] = useState<Record<string, boolean>>({});

  const toggleExpandLog = (id: string) => {
    setExpandedLogIds(prev => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    setActiveTab(resolveTab());
  }, [initialTab, subTab, userRole]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [provRes, logsRes, sysRes, clientsRes] = await Promise.all([
        fetch('/api/providers'),
        fetch('/api/audit-logs'),
        fetch('/api/system/health'),
        fetch('/api/clients')
      ]);
      if (provRes.ok) setProviders(await provRes.json());
      if (logsRes.ok) setLogs(await logsRes.json());
      if (sysRes.ok) setSystemHealth(await sysRes.json());
      if (clientsRes.ok) setClients(await clientsRes.json());
    } catch (err) {
      console.error('Error fetching admin data:', err);
    } finally {
      setLoading(false);
    }
  };

  const clientMap = useMemo(() => {
    const map = new Map<string, any>();
    clients.forEach(c => map.set(c.id, c));
    return map;
  }, [clients]);

  const providerMap = useMemo(() => {
    const map = new Map<string, any>();
    providers.forEach(p => map.set(p.id, p));
    return map;
  }, [providers]);

  const parseLogDetails = (log: AuditLog) => {
    let details: any = {};
    try {
      details = typeof log.details_json === 'string' ? JSON.parse(log.details_json) : (log.details_json || {});
    } catch {
      details = { raw: log.details_json };
    }

    // Resolve client
    const clientId = details.client_id || details.clientId || (log.entity_type === 'CLIENT' ? log.entity_id : null);
    const clientObj = clientId ? clientMap.get(clientId) : null;
    const clientName = details.client_name || details.clientName || (clientObj ? `${clientObj.first_name} ${clientObj.last_name}` : (clientId ? `Client ${clientId}` : null));

    // Resolve provider
    const providerId = details.provider_id || details.providerId || (log.entity_type === 'PROVIDER' ? log.entity_id : null);
    const providerObj = providerId ? providerMap.get(providerId) : null;
    const providerName = details.provider_name || details.providerName || details.signer || (providerObj ? providerObj.name : (providerId ? `Provider ${providerId}` : null));

    // Resolve supervisor
    const supervisorName = details.supervisor || details.supervisor_name || details.supervisorName || (details.supervisor_id ? providerMap.get(details.supervisor_id)?.name : null);

    // DOS & Service
    const dos = details.dos || details.document_date || details.service_date || null;
    const serviceId = details.service_id || details.serviceId || (log.entity_type === 'SERVICE' ? log.entity_id : null);
    const docId = details.doc_id || details.docId || (log.entity_type === 'CLINICAL_DOC' ? log.entity_id : null);

    // Headline & Category classification
    const category = getCanonicalAuditCategory(log.action, log.entity_type);
    let headline = '';
    let actionBadge = log.action.replace(/_/g, ' ');
    let badgeColor = 'bg-slate-100 text-slate-800 border-slate-300';
    let statusSummary = details.status || details.result || 'Logged';

    switch (log.action) {
      case 'LOGIN_SUCCESS':
      case 'USER_LOGIN':
      case 'AUTH_LOGIN': {
        actionBadge = 'LOGIN SUCCESS';
        badgeColor = 'bg-emerald-50 text-emerald-800 border-emerald-300';
        headline = `LOGIN SUCCESS — ${log.user_id || 'Staff'} [${log.user_role || 'STAFF'}]`;
        statusSummary = 'Authenticated';
        break;
      }
      case 'ROLE_SWITCH':
      case 'SWITCH_ROLE': {
        actionBadge = 'ROLE SWITCH';
        badgeColor = 'bg-sky-50 text-sky-800 border-sky-300';
        headline = `ROLE SWITCH — ${log.user_id} switched to [${details.newActiveRole || details.targetRole || log.user_role}]`;
        statusSummary = 'Role Activated';
        break;
      }
      case 'SWITCH_USER': {
        actionBadge = 'SWITCH USER';
        badgeColor = 'bg-sky-50 text-sky-800 border-sky-300';
        headline = `SWITCH USER — ${log.user_id} switched account to ${details.user?.email || details.email || 'User'}`;
        statusSummary = 'User Switched';
        break;
      }
      case 'LOGOUT': {
        actionBadge = 'LOGOUT';
        badgeColor = 'bg-slate-100 text-slate-800 border-slate-300';
        headline = `LOGOUT — ${log.user_id || 'Staff'}`;
        statusSummary = 'Session Closed';
        break;
      }
      case 'LOGIN_DENIED_ROSTER':
      case 'LOGIN_FAILED': {
        actionBadge = 'LOGIN DENIED';
        badgeColor = 'bg-rose-50 text-rose-800 border-rose-300';
        headline = `LOGIN DENIED — ${details.email || log.entity_id} — ${details.reason || 'Unauthorized'}`;
        statusSummary = 'Access Blocked';
        break;
      }
      case 'LOGIN_DENIED_DOMAIN': {
        actionBadge = 'LOGIN BLOCKED';
        badgeColor = 'bg-rose-50 text-rose-800 border-rose-300';
        headline = `LOGIN DENIED (DOMAIN) — ${details.email || log.entity_id} — ${details.reason || 'Non-Allegiant Google Account'}`;
        statusSummary = 'Access Blocked';
        break;
      }
      case 'WELLSKY_PRODUCTION_IMPORT': {
        actionBadge = 'DATA IMPORT';
        badgeColor = 'bg-sky-50 text-sky-800 border-sky-300';
        headline = `WELLSKY DATA IMPORT — ${details.sourceFilename || details.reportType || 'Import Completed'}`;
        statusSummary = 'Pipeline Ingest';
        break;
      }
      case 'CREATE_CLINICAL_DOC':
      case 'CREATE_SOAP':
      case 'CREATE_NOTE': {
        const docTypeLabel = (details.doc_type || details.docType || (log.action === 'CREATE_SOAP' ? 'SOAP' : 'CLINICAL DOC')).toUpperCase();
        actionBadge = `${docTypeLabel} CREATED`;
        badgeColor = 'bg-sky-50 text-sky-800 border-sky-300';
        headline = `${actionBadge} — ${clientName || 'Patient'}${providerName ? ` — Provider: ${providerName}` : ''}`;
        statusSummary = 'Draft Initiated';
        break;
      }
      case 'SIGN_CLINICIAN':
      case 'CLINICIAN_SIGNED': {
        actionBadge = 'CLINICIAN SIGNED';
        badgeColor = 'bg-emerald-50 text-emerald-800 border-emerald-300';
        headline = `CLINICIAN SIGNED — ${clientName || log.entity_id} — Provider: ${providerName || log.user_id}`;
        statusSummary = 'Clinician Signed';
        break;
      }
      case 'CREATE_CLIENT_NOTE': {
        actionBadge = 'NOTE CREATED';
        badgeColor = 'bg-sky-50 text-sky-800 border-sky-300';
        headline = `CLIENT NOTE CREATED — ${clientName || log.entity_id}`;
        statusSummary = 'Note Recorded';
        break;
      }
      case 'DELETE_CLINICAL_DOC': {
        actionBadge = 'DOC DELETED';
        badgeColor = 'bg-rose-50 text-rose-800 border-rose-300';
        headline = `CLINICAL DOC DELETED — ${details.title || details.doc_type || 'Document'} (${details.client_id || log.entity_id})`;
        statusSummary = 'Document Removed';
        break;
      }
      case 'UPLOAD_DOCUMENT': {
        actionBadge = 'DOC UPLOADED';
        badgeColor = 'bg-sky-50 text-sky-800 border-sky-300';
        headline = `DOCUMENT UPLOADED — ${details.file_name || details.doc_type || log.entity_id}${clientName ? ` (${clientName})` : ''}`;
        statusSummary = 'Uploaded';
        break;
      }
      case 'FULL_INTAKE_CREATED': {
        actionBadge = 'INTAKE CREATED';
        badgeColor = 'bg-sky-50 text-sky-800 border-sky-300';
        headline = `FULL INTAKE CREATED — ${clientName || log.entity_id}`;
        statusSummary = 'Intake Complete';
        break;
      }
      case 'DELETE_CLIENT_GOAL': {
        actionBadge = 'GOAL DELETED';
        badgeColor = 'bg-rose-50 text-rose-800 border-rose-300';
        headline = `CLIENT GOAL DELETED — Goal ${details.goal_id || log.entity_id}${clientName ? ` (${clientName})` : ''}`;
        statusSummary = 'Goal Removed';
        break;
      }
      case 'UPDATE_CLIENT_CHART': {
        actionBadge = 'CHART UPDATED';
        badgeColor = 'bg-sky-50 text-sky-800 border-sky-300';
        headline = `CLIENT CHART UPDATED — ${clientName || log.entity_id}`;
        statusSummary = 'Demographics Saved';
        break;
      }
      case 'SIGN_SUPERVISOR':
      case 'SUPERVISOR_APPROVED': {
        actionBadge = 'SUPERVISOR APPROVED';
        badgeColor = 'bg-emerald-50 text-emerald-800 border-emerald-300';
        headline = `SUPERVISOR APPROVED — ${supervisorName || providerName || 'Justin Segura, MS, CCC-SLP'} — Approved Note ${log.entity_id}`;
        statusSummary = 'Co-Signed & Cleared';
        break;
      }
      case 'RETURN_SUPERVISOR':
      case 'SUPERVISOR_RETURNED': {
        actionBadge = 'SUPERVISOR RETURNED';
        badgeColor = 'bg-rose-50 text-rose-800 border-rose-300';
        headline = `SUPERVISOR RETURNED — ${supervisorName || 'Justin Segura, MS, CCC-SLP'} — Returned Note ${log.entity_id}${details.comments ? `: "${details.comments}"` : ''}`;
        statusSummary = 'Revision Requested';
        break;
      }
      case 'APPROVE_MANUAL_SOAP_EXCEPTION': {
        actionBadge = 'SUPERVISOR EXCEPTION';
        badgeColor = 'bg-indigo-50 text-indigo-800 border-indigo-300';
        headline = `MANUAL SOAP EXCEPTION APPROVED — ${details.provider || 'Provider'} (${details.client || clientName || 'Client'})`;
        statusSummary = 'Exception Granted';
        break;
      }
      case 'REVOKE_MANUAL_SOAP_EXCEPTION': {
        actionBadge = 'EXCEPTION REVOKED';
        badgeColor = 'bg-rose-50 text-rose-800 border-rose-300';
        headline = `MANUAL SOAP EXCEPTION REVOKED — ${details.provider || 'Provider'} (${details.client || clientName || 'Client'})`;
        statusSummary = 'Exception Revoked';
        break;
      }
      case 'ASSIGN_CLIENT': {
        actionBadge = 'SUPERVISOR ASSIGNED';
        badgeColor = 'bg-indigo-50 text-indigo-800 border-indigo-300';
        headline = `SUPERVISOR & CASELOAD ASSIGNED — ${clientName || log.entity_id} — Provider: ${providerName || details.provider_id || 'Provider'}`;
        statusSummary = 'Caseload Linked';
        break;
      }
      case 'SEND_ESIGN':
      case 'DISPATCH_ESIGN': {
        actionBadge = 'GUARDIAN ESIGN SENT';
        badgeColor = 'bg-purple-50 text-purple-800 border-purple-300';
        headline = `GUARDIAN ESIGN SENT — ${clientName || log.entity_id}${details.recipient_email ? ` (${details.recipient_email})` : ''}`;
        statusSummary = 'En Route to Guardian';
        break;
      }
      case 'SIGN_GUARDIAN':
      case 'GUARDIAN_SIGNED': {
        actionBadge = details.waived ? 'GUARDIAN WAIVED' : 'GUARDIAN SIGNED';
        badgeColor = 'bg-emerald-50 text-emerald-800 border-emerald-300';
        headline = details.waived
          ? `GUARDIAN SIGNATURE WAIVED — ${clientName || log.entity_id} — Justification: ${details.signer || 'Parent documented'}`
          : `GUARDIAN SIGNED — ${clientName || log.entity_id} — Signer: ${details.signer || 'Guardian'}`;
        statusSummary = details.waived ? 'Waived / Documented' : 'Fully Signed';
        break;
      }
      case 'DISPATCH_UNIFIED_INTAKE_PACKET':
      case 'DISPATCH_INTAKE_PACKET': {
        actionBadge = 'INTAKE ESIGN DISPATCHED';
        badgeColor = 'bg-purple-50 text-purple-800 border-purple-300';
        headline = `INTAKE PACKET DISPATCHED — ${clientName || log.entity_id} — SignWell Envelope`;
        statusSummary = details.success === false ? 'Dispatch Failed' : 'En Route to Guardian';
        break;
      }
      case 'INTAKE_WEBHOOK_COMPLETED': {
        actionBadge = 'GUARDIAN ESIGN COMPLETED';
        badgeColor = 'bg-emerald-50 text-emerald-800 border-emerald-300';
        headline = `GUARDIAN ESIGN COMPLETED (SIGNWELL) — ${clientName || log.entity_id} — Envelope ${details.envelopeId || log.entity_id}`;
        statusSummary = 'Fully Executed';
        break;
      }
      case 'VOID_ESIGN_ENVELOPE':
      case 'VOID_SUPERSEDED_ENVELOPE':
      case 'INTAKE_ENVELOPE_SUPERSEDED': {
        actionBadge = 'ESIGN VOIDED';
        badgeColor = 'bg-rose-50 text-rose-800 border-rose-300';
        headline = `ESIGN ENVELOPE VOIDED — Envelope ${details.envelope_id || details.voided_envelope_id || details.oldEnvelopeId || log.entity_id}`;
        statusSummary = 'Voided';
        break;
      }
      case 'INTAKE_DISPATCH_FAILED': {
        actionBadge = 'ESIGN FAILED';
        badgeColor = 'bg-rose-50 text-rose-800 border-rose-300';
        headline = `INTAKE ESIGN DISPATCH FAILED — ${clientName || log.entity_id}`;
        statusSummary = 'Dispatch Failed';
        break;
      }
      case 'PROCESS_GUARDIAN_REMINDERS': {
        actionBadge = 'ESIGN REMINDERS';
        badgeColor = 'bg-purple-50 text-purple-800 border-purple-300';
        headline = `GUARDIAN ESIGN REMINDERS PROCESSED — Evaluated: ${details.evaluatedCount ?? 1}`;
        statusSummary = 'Job Executed';
        break;
      }
      case 'ESIGN_RECONCILIATION': {
        actionBadge = 'ESIGN RECONCILED';
        badgeColor = 'bg-purple-50 text-purple-800 border-purple-300';
        headline = `SIGNWELL ESIGN RECONCILIATION — ${details.description || 'Completed Ingest'}`;
        statusSummary = 'Reconciled';
        break;
      }
      case 'INTAKE_DETAILS_UPDATED': {
        actionBadge = 'GUARDIAN CONTACT UPDATED';
        badgeColor = 'bg-purple-50 text-purple-800 border-purple-300';
        headline = `GUARDIAN CONTACT UPDATED — ${details.guardian_email || 'Email updated'}${clientName ? ` for ${clientName}` : ''}`;
        statusSummary = 'Contact Updated';
        break;
      }
      case 'EVALUATE_BILLING_READINESS': {
        actionBadge = 'BILLING EVALUATED';
        badgeColor = 'bg-amber-50 text-amber-800 border-amber-300';
        headline = `BILLING READINESS EVALUATED — Service ${serviceId || log.entity_id}${clientName ? ` (${clientName})` : ''}`;
        statusSummary = details.status || details.readiness || details.result || (details.count !== undefined ? `${details.count} evaluated` : 'Readiness Evaluated');
        break;
      }
      case 'BILLING_READY': {
        actionBadge = 'BILLING READY';
        badgeColor = 'bg-emerald-50 text-emerald-800 border-emerald-300';
        headline = `BILLING READY — Service ${serviceId || log.entity_id}${clientName ? ` (${clientName})` : ''}`;
        statusSummary = details.status || 'Ready for EDI 837';
        break;
      }
      case 'CREATE_CLAIM_837':
      case 'GENERATE_CLAIM_837':
      case 'SUBMIT_CLAIM_837':
      case 'BATCH_837_GENERATED': {
        actionBadge = 'CLAIM 837 GENERATED';
        badgeColor = 'bg-amber-50 text-amber-800 border-amber-300';
        headline = `CLAIM 837 GENERATED — Claim #${details.claim_number || log.entity_id}${clientName ? ` — ${clientName}` : ''}${details.billed_amount ? ` ($${Number(details.billed_amount).toFixed(2)})` : ''}`;
        statusSummary = 'EDI File Produced';
        break;
      }
      case 'FINALIZE_PAYROLL': {
        actionBadge = 'PAYROLL FINALIZED';
        badgeColor = 'bg-emerald-50 text-emerald-800 border-emerald-300';
        headline = `PAYROLL FINALIZED — Period: ${details.pay_period_id || details.period_label || log.entity_id}${details.total_pay ? ` — Total: $${Number(details.total_pay).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : ''}`;
        statusSummary = 'Locked / Finalized';
        break;
      }
      case 'UPDATE_PAYROLL_ADJUSTMENT': {
        actionBadge = 'PAYROLL ADJUSTED';
        badgeColor = 'bg-amber-50 text-amber-800 border-amber-300';
        headline = `PAYROLL ADJUSTMENT — Period: ${details.pay_period_id || log.entity_id} — Manual: $${Number(details.manual_adjustment || 0).toFixed(2)}`;
        statusSummary = 'Adjustment Applied';
        break;
      }
      default: {
        actionBadge = log.action.replace(/_/g, ' ');
        badgeColor = 'bg-slate-100 text-slate-700 border-slate-300';
        headline = `${actionBadge} — ${log.entity_type} ${log.entity_id}`;
        statusSummary = 'Recorded';
        break;
      }
    }

    return {
      details,
      clientName,
      providerName,
      supervisorName,
      dos,
      serviceId,
      docId,
      headline,
      actionBadge,
      badgeColor,
      category,
      statusSummary
    };
  };

  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      const parsed = parseLogDetails(log);
      if (auditCategory !== 'all' && parsed.category !== auditCategory) {
        return false;
      }
      if (auditSearch.trim()) {
        const query = auditSearch.toLowerCase();
        const searchable = `${log.action} ${log.user_id} ${log.user_role} ${log.entity_type} ${log.entity_id} ${parsed.headline} ${parsed.clientName || ''} ${parsed.providerName || ''} ${parsed.supervisorName || ''} ${log.details_json || ''}`.toLowerCase();
        if (!searchable.includes(query)) return false;
      }
      return true;
    });
  }, [logs, auditCategory, auditSearch, clientMap, providerMap]);

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="space-y-4 sm:space-y-5">
      {/* Compressed Governance Header - Only for Audit view */}
      {activeTab === 'audit' && (
        <>
          {/* Mobile Header (< sm) - Canonical Steel-to-Navy Shell */}
          <div className="sm:hidden flex flex-col lg:flex-row lg:items-center justify-between gap-3 p-3.5 sm:p-4 rounded-2xl border mobile-header-shell">
            {/* Left: Title + FOCUS ID + Compact Subtitle */}
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold text-white tracking-tight flex flex-wrap items-center gap-2 sm:gap-2.5">
                <span className="bg-gradient-to-r from-white via-slate-100 to-slate-300 bg-clip-text text-transparent">
                  {userRole === 'SUPERVISOR' ? 'Providers & Clinical Supervision' : 'Audit History'}
                </span>
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10.5px] font-mono font-semibold bg-cyan-950/60 text-cyan-300 border border-cyan-500/30 shadow-[0_0_12px_rgba(6,182,212,0.12),inset_0_1px_0_rgba(255,255,255,0.08)] shrink-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                  FOCUS ID: 115157
                </span>
              </h1>
              <p className="text-[11px] text-slate-400 leading-tight mt-0.5 truncate">
                {userRole === 'SUPERVISOR'
                  ? 'Caseload overview, provider credentials, and clinical supervision hierarchy.'
                  : 'Operational events, system activity, and immutable audit records.'}
              </p>
            </div>

            {/* Right: Primary Database Module | Claim Transmission Gate Module | Refresh Console */}
            <div className="flex items-center flex-wrap gap-2 sm:gap-2.5 shrink-0">
              {/* Primary Database Status Module */}
              <div
                className="inline-flex items-center gap-2 px-2.5 py-1 rounded-xl bg-gradient-to-b from-[#0c1a2e]/90 via-[#07111f]/90 to-[#040810]/95 border border-emerald-500/35 hover:border-emerald-400/50 shadow-[0_2px_10px_rgba(0,0,0,0.5),0_0_14px_rgba(16,185,129,0.1),inset_0_1px_0_rgba(255,255,255,0.08)] backdrop-blur-md transition-all text-xs shrink-0"
                title="Google Cloud SQL PostgreSQL — Unified Relational Database (No secondary EMR sync gaps)"
              >
                <div className="p-1 rounded-lg bg-emerald-950/80 border border-emerald-500/40 text-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.25)]">
                  <Database className="w-3.5 h-3.5" />
                </div>
                <div className="flex flex-col justify-center leading-none">
                  <span className="text-[9.5px] font-bold uppercase tracking-wider text-slate-400">Primary Database</span>
                  <span className="text-[11px] font-mono font-semibold text-emerald-300 flex items-center gap-1.5 mt-0.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    HEALTHY • Cloud SQL
                  </span>
                </div>
              </div>

              {/* External Claim Transmission Gate Status Module */}
              <div
                className="inline-flex items-center gap-2 px-2.5 py-1 rounded-xl bg-gradient-to-b from-[#1c1407]/90 via-[#120d04]/90 to-[#080501]/95 border border-amber-500/35 hover:border-amber-400/50 shadow-[0_2px_10px_rgba(0,0,0,0.5),0_0_14px_rgba(245,158,11,0.1),inset_0_1px_0_rgba(255,255,255,0.08)] backdrop-blur-md transition-all text-xs shrink-0"
                title="Claim.MD Live Submission Hard-Gated OFF (Audit Safety Protocol — Full 837P EDI active for inspection)"
              >
                <div className="p-1 rounded-lg bg-amber-950/80 border border-amber-500/40 text-amber-400 shadow-[0_0_8px_rgba(245,158,11,0.25)]">
                  <Lock className="w-3.5 h-3.5" />
                </div>
                <div className="flex flex-col justify-center leading-none">
                  <span className="text-[9.5px] font-bold uppercase tracking-wider text-amber-400/90">Claim Transmission</span>
                  <span className="text-[11px] font-mono font-semibold text-amber-300 flex items-center gap-1.5 mt-0.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                    GATED / OFF
                  </span>
                </div>
              </div>

              {/* Refresh Console Button */}
              <button
                onClick={fetchData}
                disabled={loading}
                className="px-3 py-1.5 rounded-xl bg-gradient-to-b from-slate-800/90 to-slate-900/90 hover:from-slate-700/90 hover:to-slate-800/90 text-slate-200 hover:text-white border border-slate-700/80 hover:border-cyan-500/40 text-xs font-semibold flex items-center gap-1.5 shadow-[0_2px_8px_rgba(0,0,0,0.3)] transition-all cursor-pointer active:scale-98 shrink-0"
              >
                <RefreshCw className={`w-3.5 h-3.5 text-cyan-400 ${loading ? 'animate-spin' : ''}`} />
                <span>Refresh Console</span>
              </button>
            </div>
          </div>

          {/* Desktop/Tablet Canonical Navy Header Shell */}
          <div className="hidden sm:block relative overflow-hidden rounded-2xl border border-slate-700/80 desktop-header-shell px-6 py-4 shadow-[0_4px_16px_rgba(0,0,0,0.4),inset_0_1px_0_0_rgba(255,255,255,0.12)]">
              {/* Subtle specular top hairline */}
              <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-slate-400/30 to-transparent pointer-events-none" />
              <div className="absolute top-0 right-0 w-96 h-full bg-gradient-to-l from-sky-500/5 via-indigo-500/5 to-transparent pointer-events-none" />

              <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                {/* Left: Title + FOCUS ID + Compact Subtitle */}
                <div className="min-w-0">
                  <h1 className="text-xl font-bold text-white tracking-tight flex flex-wrap items-center gap-2.5">
                    <span>Audit History</span>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10.5px] font-mono font-semibold bg-sky-500/10 text-sky-300 border border-sky-500/25 shrink-0">
                      <span className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-pulse" />
                      FOCUS ID: 115157
                    </span>
                  </h1>
                  <p className="text-xs text-slate-400 leading-tight mt-1 truncate">
                    Operational events, system activity, and immutable audit records.
                  </p>
                </div>

                {/* Right: Primary Database Module | Claim Transmission Gate Module | Refresh Console */}
                <div className="flex items-center flex-wrap gap-2.5 shrink-0">
                  {/* Primary Database Status Module */}
                  <div
                    className="inline-flex items-center gap-2 px-2.5 py-1 rounded-xl bg-gradient-to-b from-[#0c1a2e]/90 via-[#07111f]/90 to-[#040810]/95 border border-emerald-500/35 hover:border-emerald-400/50 shadow-[0_2px_10px_rgba(0,0,0,0.5),0_0_14px_rgba(16,185,129,0.1),inset_0_1px_0_rgba(255,255,255,0.08)] backdrop-blur-md transition-all text-xs shrink-0"
                    title="Google Cloud SQL PostgreSQL — Unified Relational Database (No secondary EMR sync gaps)"
                  >
                    <div className="p-1 rounded-lg bg-emerald-950/80 border border-emerald-500/40 text-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.25)]">
                      <Database className="w-3.5 h-3.5" />
                    </div>
                    <div className="flex flex-col justify-center leading-none">
                      <span className="text-[9.5px] font-bold uppercase tracking-wider text-slate-400">Primary Database</span>
                      <span className="text-[11px] font-mono font-semibold text-emerald-300 flex items-center gap-1.5 mt-0.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                        HEALTHY • Cloud SQL
                      </span>
                    </div>
                  </div>

                  {/* External Claim Transmission Gate Status Module */}
                  <div
                    className="inline-flex items-center gap-2 px-2.5 py-1 rounded-xl bg-gradient-to-b from-[#1c1407]/90 via-[#120d04]/90 to-[#080501]/95 border border-amber-500/35 hover:border-amber-400/50 shadow-[0_2px_10px_rgba(0,0,0,0.5),0_0_14px_rgba(245,158,11,0.1),inset_0_1px_0_rgba(255,255,255,0.08)] backdrop-blur-md transition-all text-xs shrink-0"
                    title="Claim.MD Live Submission Hard-Gated OFF (Audit Safety Protocol — Full 837P EDI active for inspection)"
                  >
                    <div className="p-1 rounded-lg bg-amber-950/80 border border-amber-500/40 text-amber-400 shadow-[0_0_8px_rgba(245,158,11,0.25)]">
                      <Lock className="w-3.5 h-3.5" />
                    </div>
                    <div className="flex flex-col justify-center leading-none">
                      <span className="text-[9.5px] font-bold uppercase tracking-wider text-amber-400/90">Claim Transmission</span>
                      <span className="text-[11px] font-mono font-semibold text-amber-300 flex items-center gap-1.5 mt-0.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                        GATED / OFF
                      </span>
                    </div>
                  </div>

                  {/* Refresh Console Button */}
                  <button
                    onClick={fetchData}
                    disabled={loading}
                    className="px-3 py-1.5 rounded-xl bg-gradient-to-b from-slate-800/90 to-slate-900/90 hover:from-slate-700/90 hover:to-slate-800/90 text-slate-200 hover:text-white border border-slate-700/80 hover:border-slate-600 text-xs font-semibold flex items-center gap-1.5 shadow-[0_2px_8px_rgba(0,0,0,0.3)] transition-all cursor-pointer active:scale-98 shrink-0"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 text-sky-400 ${loading ? 'animate-spin' : ''}`} />
                    <span>Refresh Console</span>
                  </button>
                </div>
              </div>
            </div>
        </>
      )}

      {/* Sub-Navigation Tabs - Multi-tab switch for Admin and Owner */}
      {(userRole === 'ADMIN' || userRole === 'OWNER') && (
        <div className="flex flex-wrap items-center gap-1.5 p-1 rounded-xl pulse-control-shell w-fit mb-2">
          <button
            id="tab-admin-connections"
            onClick={() => setActiveTab('connections')}
            className={`px-3.5 py-2 rounded-lg text-xs font-bold flex items-center space-x-2 transition-all ${
              activeTab === 'connections'
                ? 'bg-sky-500/10 text-sky-400 border border-sky-500/30 shadow-sm'
                : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
            }`}
          >
            <Radio className="w-3.5 h-3.5" />
            <span>Connections & Billing Setup</span>
          </button>

          <button
            id="tab-admin-supervision"
            onClick={() => setActiveTab('supervision')}
            className={`px-3.5 py-2 rounded-lg text-xs font-bold flex items-center space-x-2 transition-all ${
              activeTab === 'supervision'
                ? 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/30 shadow-sm'
                : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
            }`}
          >
            <UserCheck className="w-3.5 h-3.5" />
            <span>Providers & Supervision</span>
          </button>

          <button
            id="tab-admin-audit"
            onClick={() => setActiveTab('audit')}
            className={`px-3.5 py-2 rounded-lg text-xs font-bold flex items-center space-x-2 transition-all ${
              activeTab === 'audit'
                ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 shadow-sm'
                : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
            }`}
          >
            <Activity className="w-3.5 h-3.5" />
            <span>Audit Trail & Gates</span>
          </button>

          {userRole === 'OWNER' && (
            <button
              id="tab-admin-ai-usage"
              onClick={() => setActiveTab('ai-usage')}
              className={`hidden md:flex px-3.5 py-2 rounded-lg text-xs font-bold items-center space-x-2 transition-all cursor-pointer ${
                activeTab === 'ai-usage'
                  ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 shadow-sm'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
              }`}
            >
              <Activity className="w-3.5 h-3.5 text-emerald-400" />
              <span>AI & Data Usage</span>
            </button>
          )}
        </div>
      )}

      {/* TAB: AI & Data Usage (Desktop/Tablet Owner only) */}
      {activeTab === 'ai-usage' && userRole === 'OWNER' && (
        <OwnerAiDataUsageView />
      )}

      {/* TAB 1: Connections & Billing Setup */}
      {activeTab === 'connections' && (
        <AdminConnectionsSetupView
          onNavigateToAuthorizations={() => onNavigateToTab?.('authorizations')}
        />
      )}

      {/* TAB 2: Providers & Supervision */}
      {activeTab === 'supervision' && (
        <ProviderManagementView
          userRole={userRole}
          onNavigateToTab={onNavigateToTab}
          embedded
        />
      )}

      {/* TAB 3: Audit Trail & Gates */}
      {activeTab === 'audit' && (
        <div className="space-y-4">
          {/* Dense Polished Audit Trail Section */}
          <div className="glass-panel rounded-xl overflow-hidden border border-slate-200 shadow-xs">
            {/* AUDIT TRAIL HEADER ROW */}
            <div className="px-4 py-2.5 border-b border-slate-800/80 bg-gradient-to-r from-[#0a1324]/95 via-[#070d1a]/95 to-[#050912]/98 backdrop-blur-md relative flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
              {/* Subtle cyan top-edge highlight */}
              <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent pointer-events-none" />

              <div className="flex items-center space-x-2">
                <Activity className="w-4 h-4 text-cyan-400 shrink-0" />
                <h2 className="text-xs font-bold uppercase tracking-widest text-slate-100">
                  Audit Trail
                </h2>
              </div>
              <div className="flex items-center space-x-3 text-[11px]">
                <span className="text-slate-400 font-mono">Showing {filteredLogs.length} of {logs.length} logged events</span>
                <span className="text-[10px] text-emerald-300 font-mono px-2 py-0.5 rounded-md bg-emerald-950/70 border border-emerald-500/35 font-bold shadow-[0_0_8px_rgba(16,185,129,0.15)] flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  HIPAA Immutable WORM
                </span>
              </div>
            </div>

            {/* Filter & Search Bar with Neutral Controls */}
            <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50 flex flex-col md:flex-row gap-2.5 md:items-center justify-between">
              {/* Pulse Audit Search Bar */}
              <div className="relative rounded-xl bg-white border border-slate-300 hover:border-slate-400 focus-within:border-slate-500 focus-within:ring-1 focus-within:ring-slate-400 shadow-xs transition-all overflow-hidden group w-full md:w-80 lg:w-96 shrink-0">
                <div className="relative z-10 flex items-center px-2.5 py-1.5 gap-2">
                  <div className="flex sm:hidden items-center pl-0.5 shrink-0 select-none text-slate-400">
                    <Search className="w-3.5 h-3.5 shrink-0" />
                  </div>
                  <div className="hidden sm:flex relative items-center shrink-0 select-none" title="Pulse Audit Search">
                    <PulseIntelligenceMark
                      size={18}
                      active={auditSearch.trim().length > 0}
                      className="shrink-0"
                    />
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-600 animate-pulse ml-1.5 mr-1 shrink-0" title="Live filter active" />
                    <div className="h-3.5 w-px bg-slate-300 mr-1 shrink-0" />
                  </div>

                  {/* Input Field: Search audit events... */}
                  <input
                    type="text"
                    value={auditSearch}
                    onChange={(e) => setAuditSearch(e.target.value)}
                    placeholder="Search audit events..."
                    className="flex-1 bg-transparent text-slate-900 text-xs placeholder:text-slate-400 focus:outline-none focus:ring-0 min-w-0 font-sans tracking-tight py-0.5"
                    autoComplete="off"
                    spellCheck="false"
                  />

                  {/* Clear button */}
                  {auditSearch.trim() && (
                    <button
                      type="button"
                      onClick={() => setAuditSearch('')}
                      className="text-slate-400 hover:text-slate-600 p-0.5 rounded transition-colors shrink-0"
                      title="Clear search"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Polished Audit Filter Tabs */}
              <div className="flex items-center flex-wrap gap-1 sm:gap-1.5 p-1 rounded-xl bg-slate-200/80 border border-slate-300/80 w-full md:w-auto">
                {[
                  { id: 'all', label: 'All Events' },
                  { id: 'clinical', label: 'Notes & Clinical' },
                  { id: 'supervision', label: 'Supervision' },
                  { id: 'esign', label: 'Guardian eSign' },
                  { id: 'billing', label: 'Billing & 837' },
                  { id: 'payroll', label: 'Payroll' },
                  { id: 'system', label: 'System' }
                ].map((tab) => {
                  const isSelected = auditCategory === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setAuditCategory(tab.id)}
                      className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold tracking-tight transition-all duration-150 cursor-pointer ${
                        isSelected
                          ? 'bg-[#162030] text-white border border-slate-700 shadow-xs'
                          : 'bg-white text-slate-700 border border-slate-200 hover:border-slate-300 hover:text-slate-900 hover:bg-slate-50'
                      }`}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Events List - Compact Operational Records with Hairline Separators */}
            <div className="divide-y divide-slate-200 bg-white">
              {filteredLogs.length === 0 ? (
                <div className="p-12 text-center text-slate-600 text-xs bg-white">
                  No audit logs match the current search filter.
                </div>
              ) : (
                filteredLogs.map((l, lIdx: number) => {
                  const parsed = parseLogDetails(l);
                  const isExpanded = expandedLogIds[l.id];

                  const categoryBorderColor = 
                    l.action === 'LOGIN_SUCCESS' || l.action === 'USER_LOGIN' || l.action === 'AUTH_LOGIN'
                      ? 'border-l-emerald-500'
                      : l.action === 'ROLE_SWITCH' || l.action === 'SWITCH_ROLE'
                      ? 'border-l-sky-500'
                      : parsed.category === 'clinical'
                      ? 'border-l-sky-500'
                      : parsed.category === 'supervision'
                      ? 'border-l-indigo-500'
                      : parsed.category === 'esign'
                      ? 'border-l-purple-500'
                      : parsed.category === 'billing'
                      ? 'border-l-amber-500'
                      : parsed.category === 'payroll'
                      ? 'border-l-teal-500'
                      : 'border-l-slate-400';

                  return (
                    <div
                      key={l.id}
                      tabIndex={0}
                      role="button"
                      onClick={() => toggleExpandLog(l.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleExpandLog(l.id);
                        }
                      }}
                      className={`group relative px-3.5 py-2 sm:py-2.5 transition-colors duration-150 cursor-pointer select-none border-l-2 ${categoryBorderColor} focus:outline-none focus:bg-slate-100 ${
                        isExpanded
                          ? 'bg-slate-100/90'
                          : lIdx % 2 === 0
                          ? 'bg-white hover:bg-slate-50'
                          : 'bg-slate-50/60 hover:bg-slate-100/80'
                      }`}
                    >
                      {/* Compact Primary Row */}
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 sm:gap-2">
                        {/* Left Cluster: Action badge, Event headline (strongest), user/role tags */}
                        <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-1">
                          <span className={`text-[9px] sm:text-[9.5px] font-mono px-1.5 sm:px-2 py-0.5 rounded font-bold uppercase tracking-wider shrink-0 border shadow-xs ${parsed.badgeColor}`}>
                            {parsed.actionBadge}
                          </span>

                          <span className="font-semibold text-xs text-slate-900 truncate group-hover:text-cyan-700 transition-colors" title={parsed.headline}>
                            {parsed.headline}
                          </span>

                          <span className="text-[9.5px] font-mono text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200 shrink-0 hidden sm:inline-flex items-center gap-1">
                            <span className="text-slate-600">User:</span>
                            <strong className="text-slate-800 font-semibold">{l.user_id}</strong>
                            <span className="text-sky-700 font-bold">[{l.user_role}]</span>
                          </span>
                        </div>

                        {/* Right Cluster: Status pill, Timestamp, Technical Evidence Action */}
                        <div className="flex items-center justify-between sm:justify-end gap-2 shrink-0">
                          <span className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-[9.5px] font-mono text-slate-700 font-medium">
                            {parsed.statusSummary}
                          </span>

                          <span className="font-mono text-slate-600 text-[10px] hidden md:inline-flex items-center gap-1">
                            <Clock className="w-2.5 h-2.5 text-slate-500" />
                            <span>{l.timestamp}</span>
                          </span>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleExpandLog(l.id);
                            }}
                            className={`group/btn relative flex items-center space-x-1 text-[9.5px] font-mono transition-colors duration-150 px-2 py-0.5 rounded border cursor-pointer ${
                              isExpanded
                                ? 'bg-cyan-100 border-cyan-400 text-cyan-800 font-semibold'
                                : 'bg-white border-slate-300 text-slate-700 hover:text-cyan-800 hover:border-slate-400 hover:bg-slate-50'
                            }`}
                            title={isExpanded ? 'Hide Raw Details' : 'View Technical Evidence'}
                          >
                            <Eye className={`w-2.5 h-2.5 ${isExpanded ? 'text-cyan-700' : 'text-slate-500 group-hover/btn:text-cyan-700'}`} />
                            <span className="hidden sm:inline">{isExpanded ? 'Hide Evidence' : 'View Evidence'}</span>
                            <span className="sm:hidden">{isExpanded ? 'Hide' : 'Evidence'}</span>
                            {isExpanded ? (
                              <ChevronUp className="w-2.5 h-2.5 text-cyan-700" />
                            ) : (
                              <ChevronDown className="w-2.5 h-2.5 text-slate-500 group-hover/btn:text-cyan-700" />
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Compact Metadata Sub-row: Timestamp on small screens, Client/Provider/Target/DOS */}
                      <div className="flex flex-wrap items-center gap-1.5 text-[9.5px] font-mono text-slate-600 mt-1.5 sm:mt-2 leading-normal">
                        <span className="font-mono text-slate-600 text-[9.5px] md:hidden flex items-center gap-1">
                          <Clock className="w-2.5 h-2.5 text-slate-500" />
                          <span>{l.timestamp}</span>
                          <span className="text-slate-300">•</span>
                        </span>

                        <span className="text-[9.5px] font-mono text-slate-500 sm:hidden">
                          {l.user_id} <span className="text-sky-700 font-semibold">[{l.user_role}]</span>
                          <span className="text-slate-300 ml-1">•</span>
                        </span>

                        {parsed.clientName && (
                          <span className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 flex items-center space-x-1">
                            <User className="w-2.5 h-2.5 text-sky-600" />
                            <span>Client: <strong className="text-slate-800 font-sans">{parsed.clientName}</strong></span>
                          </span>
                        )}

                        {parsed.providerName && (
                          <span className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 flex items-center space-x-1">
                            <UserCheck className="w-2.5 h-2.5 text-emerald-600" />
                            <span>Provider: <strong className="text-slate-800 font-sans">{parsed.providerName}</strong></span>
                          </span>
                        )}

                        {parsed.supervisorName && (
                          <span className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 flex items-center space-x-1">
                            <ShieldCheck className="w-2.5 h-2.5 text-indigo-600" />
                            <span>Supervisor: <strong className="text-slate-800 font-sans">{parsed.supervisorName}</strong></span>
                          </span>
                        )}

                        {parsed.dos && (
                          <span className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200">
                            DOS: <strong className="text-slate-800">{parsed.dos}</strong>
                          </span>
                        )}

                        <span className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-700">
                          Target: {l.entity_type} #{l.entity_id}
                        </span>
                      </div>

                      {/* Expandable Technical Details View (Raw JSON & Detailed Field Inspector) */}
                      {isExpanded && (
                        <div
                          onClick={(e) => e.stopPropagation()}
                          className="mt-2 p-3 rounded-md bg-white border border-slate-200 space-y-2 font-mono text-[11px] shadow-xs"
                        >
                          <div className="flex items-center justify-between text-[10px] text-slate-500 border-b border-slate-200 pb-1.5">
                            <span className="font-bold text-sky-700 uppercase tracking-wider">
                              Technical Audit Evidence Record #{l.id}
                            </span>
                            <span>Recorded at: {l.timestamp}</span>
                          </div>

                          {/* Key-Value Breakdown */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-[10px]">
                            <div className="p-2 rounded bg-slate-50 border border-slate-200">
                              <span className="text-slate-500 block">Action Code:</span>
                              <strong className="text-slate-800">{l.action}</strong>
                            </div>
                            <div className="p-2 rounded bg-slate-50 border border-slate-200">
                              <span className="text-slate-500 block">Entity:</span>
                              <strong className="text-slate-800">{l.entity_type} ({l.entity_id})</strong>
                            </div>
                            <div className="p-2 rounded bg-slate-50 border border-slate-200">
                              <span className="text-slate-500 block">Operator:</span>
                              <strong className="text-slate-800">{l.user_id} [{l.user_role}]</strong>
                            </div>
                            <div className="p-2 rounded bg-slate-50 border border-slate-200">
                              <span className="text-slate-500 block">Compliance Status:</span>
                              <strong className="text-emerald-700 font-bold">VERIFIED IMMUTABLE</strong>
                            </div>
                          </div>

                          {/* Raw JSON Code Block */}
                          <div>
                            <span className="text-[10px] text-slate-600 block mb-1 font-sans font-semibold">
                              Raw JSON Payload (`details_json`):
                            </span>
                            <pre className="p-3 rounded bg-slate-50 border border-slate-200 text-[10px] text-slate-800 overflow-x-auto whitespace-pre-wrap break-all">
                              {JSON.stringify(parsed.details, null, 2)}
                            </pre>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
